// What a prescription implies about time, and whether the clock agrees.
//
// Nothing here counts anything down or ticks. It is pure arithmetic: given a
// tempo and a rep target, how long the set should take; given what was
// prescribed and what the clock actually read, whether it fell short. The
// components that own a running clock (RestTimer, SessionClock) call these —
// they do not reimplement them.

import { tempoPhases } from './execution';

/**
 * The time a set should take, from its tempo and rep target.
 *
 * A tempo phase of "X" is explosive — as fast as the athlete can move — and has
 * no fixed duration by definition, so it contributes nothing to the sum. That
 * can legitimately total 0 (an all-"X" tempo): a real answer, not a missing one,
 * which is why the caller distinguishes it from null rather than this function
 * doing so.
 *
 * @param {string} tempo four-phase notation, e.g. "4-0-1-0"
 * @param {number|string} reps the resolved target for this set — a single
 *   count, not a scheme. A range ("8-10") or a non-numeric scheme ("AMRAP")
 *   has already failed to resolve by the time it reaches here, and is refused
 *   rather than guessed at.
 * @returns {number|null} seconds, or null when either input doesn't resolve
 *   to a computable duration — no tempo, a malformed tempo, or reps that are
 *   not a positive whole number.
 */
export function expectedWorkSeconds(tempo, reps) {
  const phases = tempoPhases(tempo);
  if (!phases) return null;

  const repCount = Number(reps);
  if (!Number.isFinite(repCount) || !Number.isInteger(repCount) || repCount <= 0) return null;

  const perRep = phases.reduce(
    (sum, phase) => sum + (phase.seconds === 'X' ? 0 : Number(phase.seconds)),
    0
  );
  return perRep * repCount;
}

/**
 * Whether an actual duration fell meaningfully short of what was prescribed.
 *
 * One function for two questions — "was this set rushed past its tempo?" and
 * "was this rest cut short?" — because both are the same shape: an expected
 * number, an actual number, and a tolerance below which it no longer reads as
 * noise. Written and tested once rather than twice so the two can't quietly
 * drift to different thresholds.
 *
 * Deliberately one-directional. Taking LONGER than expected is never flagged —
 * RestTimer's own rule ("a prescribed 120s is a floor, not a deadline") applies
 * here too, and a slow, grinding rep is not a problem. Only short counts.
 *
 * @param {number|null} expectedSeconds
 * @param {number} actualSeconds
 * @param {number} tolerance fraction below expected that still reads as
 *   on-pace, e.g. 0.2 forgives up to 20% short. Not measured against real
 *   sessions yet — treat as tunable.
 * @returns {boolean|null} null when there is nothing to fall short of —
 *   expectedSeconds is null or 0 (an all-"X" tempo, or no tempo/rest
 *   prescribed at all) — or actualSeconds isn't a usable number.
 */
export function fellShortOfPrescribed(expectedSeconds, actualSeconds, tolerance = 0.2) {
  if (expectedSeconds === null || expectedSeconds === undefined) return null;
  if (!(expectedSeconds > 0)) return null;

  const actual = Number(actualSeconds);
  if (!Number.isFinite(actual)) return null;

  return actual < expectedSeconds * (1 - tolerance);
}
